// src/components/frameitforward/FutureGiftModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon, ArrowLeft, PlusIcon } from '../../public/icons';
import { MagnetsFrame, AudioGift, DigitalMemory } from '../../public/images/shop';

/* ─────────────────────── Draft persistence ─────────────────────── */
const STEP_DRAFT_KEY = 'kapta_frameitforward_step_futuregift';

const loadStepDraft = () => {
  try {
    const raw = localStorage.getItem(STEP_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load future gift draft:', err);
    return null;
  }
};

const saveStepDraft = (data) => {
  try {
    localStorage.setItem(STEP_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save future gift draft:', err);
  }
};

/* ─────────────────────── Gift Type Options ─────────────────────── */
const GIFT_TYPES = [
  {
    id: 'magnets-frame',
    name: 'Magnets + Frame',
    price: 20000,
    image: MagnetsFrame,
    alt: 'Framed magnets set'
  },
  {
    id: 'audio-gift',
    name: 'Audio Gift',
    price: 20000,
    image: AudioGift,
    alt: 'Wooden alarm clock with audio'
  },
  {
    id: 'digital-memory',
    name: 'Digital Memory',
    price: 20000,
    image: DigitalMemory,
    alt: 'Digital memory book'
  }
];

/* ─────────────────────── Future Gift Form ─────────────────────── */
const FutureGiftForm = ({ initialData, onSave, onBack }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [deliveryDate, setDeliveryDate] = useState(initialData?.deliveryDate || '');
  const [giftType, setGiftType] = useState(initialData?.giftType || '');
  const [errors, setErrors] = useState({});

  const handleSave = () => {
    const next = {};
    if (!deliveryDate) next.deliveryDate = 'Please choose a delivery date';
    if (!giftType) next.giftType = 'Please choose a gift type';

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSave({
      title: title.trim() || 'Future Gift',
      deliveryDate,
      giftType
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-start justify-between px-4 sm:px-6 pt-6 pb-4 border-b border-[#DBDBDB]">
        <div className="text-left">
          <h2 className="text-xl sm:text-2xl font-bold text-[#1C2A46]">
            Set up your Future Gift
          </h2>
          <p className="text-[#6B635D] text-sm mt-1">
            Choose what to gift and when it should be revealed.
          </p>
        </div>
        <button
          onClick={onBack}
          className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
          aria-label="Close"
        >
          <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 text-left">
        <div className="mb-6">
          <label className="block text-base font-normal text-[#1C2A46] mb-2">
            Future Gift Title
          </label>
          <input
            type="text"
            placeholder="For our Anniversary"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            className="w-full px-4 py-3 border border-[#E4E4E4] bg-transparent placeholder-[#6B635D] placeholder:font-light placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
          />
        </div>

        <div className="mb-2">
          <label className="block text-base font-normal text-[#1C2A46] mb-2">
            Future Delivery Date*
          </label>
          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => {
              setDeliveryDate(e.target.value);
              setErrors((p) => ({ ...p, deliveryDate: '' }));
            }}
            className={`w-full px-4 py-3 border ${
              errors.deliveryDate ? 'border-red-400' : 'border-[#E4E4E4]'
            } bg-transparent text-[#1C2A46] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent`}
          />
          {errors.deliveryDate && (
            <p className="mt-1 text-xs text-red-600">{errors.deliveryDate}</p>
          )}
        </div>

        <p className="text-xs text-[#6B635D] mb-6 flex items-start gap-2">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[#6B635D] text-[10px] mt-0.5">
            i
          </span>
          Digital gifts unlock on this date while Physical gifts are prepared and
          delivered around this date.
        </p>

        <div className="mb-6">
          <h3 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-1">
            What kind of gift is this?
          </h3>
          <p className="text-sm text-[#6B635D] mb-4">
            Choose how you'd like this gift to continue beyond today.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {GIFT_TYPES.map((type) => {
              const selected = giftType === type.id;
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    setGiftType(type.id);
                    setErrors((p) => ({ ...p, giftType: '' }));
                  }}
                  className={`border text-left transition-colors ${
                    selected
                      ? 'border-[#55000C]'
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img
                    src={type.image}
                    alt={type.alt}
                    className="w-full h-40 sm:h-44 object-cover"
                  />
                  <div className="flex justify-between items-center px-3 py-3">
                    <span className="text-sm sm:text-base font-medium text-[#1C2A46]">
                      {type.name}
                    </span>
                    <span className="text-sm text-[#389B51] font-normal">
                      +₦{type.price.toLocaleString()}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {errors.giftType && (
            <p className="mt-2 text-xs text-red-600">{errors.giftType}</p>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6 border-t border-[#DBDBDB] flex gap-3 sm:gap-4">
        <button
          onClick={onBack}
          className="flex w-1/3 sm:w-1/4 justify-center items-center gap-2 py-3 font-medium border border-[#DBDBDB] text-[#1C2A46] hover:border-[#D2A146] transition-colors"
        >
          <ArrowLeft />
          Back
        </button>
        <button
          onClick={handleSave}
          className="flex-1 py-3 font-medium bg-[#D2A146] text-white hover:bg-[#C49660] transition-colors"
        >
          Save future gift
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────── Filled Slot Card ─────────────────────── */
const FilledSlot = ({ index, data, onEdit, onRemove }) => {
  const giftLabel =
    GIFT_TYPES.find((t) => t.id === data.giftType)?.name || '—';

  const formattedDate = data.deliveryDate
    ? new Date(data.deliveryDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : '—';

  const hasContent = !!data.title;

  return (
    <div className="border border-dashed border-[#DBDBDB] bg-white p-4 sm:p-5 flex flex-col text-left">
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#F0F0F0]">
        <span className="text-sm sm:text-base font-medium text-[#1C2A46]">
          Future Gift {index + 1}
        </span>
        <span className="text-[10px] sm:text-xs text-[#6B635D] bg-[#F5F5F5] px-2 py-0.5 rounded">
          Scheduled
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-[#6B635D]">Gift</span>
          <span className="text-[#1C2A46] text-right">{giftLabel}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#6B635D]">Future Date</span>
          <span className="text-[#1C2A46] text-right">{formattedDate}</span>
        </div>
        <div className="flex justify-between text-sm items-center">
          <span className="text-[#6B635D]">Content</span>
          {hasContent ? (
            <span className="text-[#1C2A46] text-right truncate max-w-[140px]">
              {data.title}
            </span>
          ) : (
            <span className="text-[#B8860B] text-right flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Not added
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-4 mt-auto pt-2">
        <button
          onClick={() => onEdit(index)}
          className="text-sm text-[#1C2A46] underline hover:text-[#55000C]"
        >
          Edit
        </button>
        <button
          onClick={() => onRemove(index)}
          className="text-sm text-[#A82121] underline hover:text-[#55000C]"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────── Empty Slot Card ─────────────────────── */
const EmptySlot = ({ index, onAdd }) => (
  <button
    type="button"
    onClick={() => onAdd(index)}
    className="border border-dashed border-[#DBDBDB] bg-transparent min-h-[200px] sm:min-h-[240px] flex flex-col items-center justify-center gap-3 hover:border-[#55000C] transition-colors"
  >
    <PlusIcon size={32} color="#6B635D" />
    <div className="text-center">
      <div className="text-sm sm:text-base font-medium text-[#1C2A46]">
        Add Future Gift {index + 1}
      </div>
      <div className="text-xs text-[#6B635D] mt-0.5">Not added yet</div>
    </div>
  </button>
);

/* ─────────────────────── Main Modal ─────────────────────── */
const FutureGiftModal = ({
  isOpen,
  onClose,
  onBack,
  onContinue,
  onProgressChange
}) => {
  const [slots, setSlots] = useState([null, null, null]);
  const [activeSlot, setActiveSlot] = useState(null);

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on open ───────── */
  useEffect(() => {
    if (!isOpen) return;

    const draft = loadStepDraft();
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (Array.isArray(draft.slots)) setSlots(draft.slots);
    if (draft.activeSlot !== undefined) setActiveSlot(draft.activeSlot);

    draftHydratedRef.current = true;
  }, [isOpen]);

  /* ───────── AUTOSAVE + broadcast on every change ───────── */
  useEffect(() => {
    if (!isOpen) return;
    if (!draftHydratedRef.current) return;

    const draft = { slots, activeSlot };

    saveStepDraft(draft);
    onProgressChange?.(draft);
  }, [isOpen, slots, activeSlot, onProgressChange]);

  if (!isOpen) return null;

  const openAddForm = (index) => setActiveSlot(index);
  const openEditForm = (index) => setActiveSlot(index);
  const closeForm = () => setActiveSlot(null);

  const handleSaveSlot = (data) => {
    setSlots((prev) => {
      const next = [...prev];
      next[activeSlot] = data;
      return next;
    });
    setActiveSlot(null);
  };

  const handleRemoveSlot = (index) => {
    if (!window.confirm('Remove this future gift?')) return;
    setSlots((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const handleContinue = () => {
    const added = slots.filter(Boolean);
    onContinue({ futureGifts: added });
    onClose();
  };

  const filledCount = slots.filter(Boolean).length;
  const isFormOpen = activeSlot !== null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        {isFormOpen ? (
          <FutureGiftForm
            initialData={slots[activeSlot]}
            onSave={handleSaveSlot}
            onBack={closeForm}
          />
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex items-start justify-between px-4 sm:px-6 pt-6 pb-4 border-b border-[#DBDBDB]">
              <div className="text-left">
                <h2 className="text-xl sm:text-2xl font-bold text-[#1C2A46]">
                  Add Future Gifts
                </h2>
                <p className="text-[#6B635D] text-sm mt-1">
                  Schedule up to 3 special moments to be revealed or delivered
                  later.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
                aria-label="Close"
              >
                <CloseIcon className="w-5 h-5 text-[#1C2A46]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 text-left">
              <h3 className="text-base sm:text-lg font-medium text-[#1C2A46]">
                What would you like to add next?
              </h3>
              <p className="text-sm text-[#6B635D] mb-6 mt-1">
                Select each slot and add a future gift that'll be delivered in a
                future date.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {slots.map((slot, index) =>
                  slot ? (
                    <FilledSlot
                      key={index}
                      index={index}
                      data={slot}
                      onEdit={openEditForm}
                      onRemove={handleRemoveSlot}
                    />
                  ) : (
                    <EmptySlot
                      key={index}
                      index={index}
                      onAdd={openAddForm}
                    />
                  )
                )}
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-[#DBDBDB] flex gap-3 sm:gap-4">
              <button
                onClick={onBack}
                className="flex w-1/3 sm:w-1/4 justify-center items-center gap-2 py-3 font-medium border border-[#DBDBDB] text-[#1C2A46] hover:border-[#D2A146] transition-colors"
              >
                <ArrowLeft />
                Back
              </button>
              <button
                onClick={handleContinue}
                className='flex-1 py-3 font-medium transition-colors bg-[#D2A146] text-white hover:bg-[#C49660]'
              >
                Continue to cart
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FutureGiftModal;